/**
 * Ruby AI catalogue model. PURE — no React, no I/O.
 *
 * Everything the Providers & Models, Ruby AI Actions, Vibes and Ruby Tokens
 * tabs derive from registry rows lives here so it is unit-tested once:
 * "used for" chips, the bill unit, vendor price text, which models an action
 * may use, an action's LIVE / NOT WIRED / TO MAP status, the one pricing
 * formula (spec §3) for estimates, and tier gates read from the AI family.
 */
import type { AiModelDto, AiUnitPricesDto, AiUsageCountsDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiActionGroupId, AiActionStepDto, AiVibeDto, ModelRefDto } from '../../../services/aiControl/rubyAi';
import { liveTokensPerCredit } from './units';

// ---------- "Used for" (MODEL#.capabilities) ----------

export const USED_FOR_LABELS: Record<string, string> = {
  text: 'Text',
  web_search: 'Web search',
  // Legacy, ambiguous — kept so an already-stored row still has a label, but
  // never offered as a pressable chip (see NEEDS_REVIEW_CAPABILITIES below:
  // the Ruby Generation Engine, 2026-10-04, split it into four capabilities).
  image: 'Image',
  image_edit: 'Image edit',
  draws_images: 'Draws images',
  edits_images: 'Edits images · background',
  finds_images: 'Finds images on the web',
  reads_images: 'Reads images',
  video: 'Video',
  voice: 'Voice',
  long_form: 'Legal / long-form',
  // 2026-10-15 — Ruby Estate property insights data sources
  property_data: 'Property data',
  area_data: 'Area data',
  maps: 'Maps',
};

/**
 * Capabilities written before the "used for" vocabulary existed. They are
 * READ as their used-for equivalent (the stored value is never rewritten
 * unless an admin saves the model) so today's rows filter correctly.
 *
 * 2026-10-04 (Ruby Generation Engine): mirrors packages/ai-router's
 * `normalizeCapabilities` ALIASES table exactly — `image_generation` and the
 * old literal `image_edit` resolve to the new draw/edit capabilities, and
 * `vision` / `image_analysis` resolve to "reads images". Plain `image` is
 * deliberately NOT an alias — it stays ambiguous (could mean draw, edit,
 * find or read) and is flagged instead, see NEEDS_REVIEW_CAPABILITIES.
 */
export const LEGACY_CAPABILITY_ALIASES: Record<string, string> = {
  text_generation: 'text',
  chat: 'text',
  grounding: 'web_search',
  image_generation: 'draws_images',
  image_edit: 'edits_images',
  video_generation: 'video',
  vision: 'reads_images',
  image_analysis: 'reads_images',
};

/**
 * Ambiguous legacy capability strings that must never resolve to a used-for
 * value — mirrors packages/ai-router's `REVIEW` set. A model stuck with one
 * of these shows a "needs review" chip instead of a pressable capability.
 * It is KEPT on save until an admin explicitly resolves it (ProviderModals'
 * `ModelEditModal`: turning on one of the four image capabilities, or
 * "Not an image model") — an untouched save must never silently drop it.
 */
export const NEEDS_REVIEW_CAPABILITIES: readonly string[] = ['image'];

export type UsedForTone = 'ruby' | 'orange' | 'blue' | 'teal' | 'stone';

export const USED_FOR_TONE: Record<string, UsedForTone> = {
  text: 'ruby', web_search: 'ruby', long_form: 'ruby',
  image: 'orange', image_edit: 'orange',
  draws_images: 'orange', edits_images: 'orange', finds_images: 'orange', reads_images: 'orange',
  video: 'blue',
  voice: 'teal',
  property_data: 'stone', area_data: 'stone', maps: 'stone',
};

/**
 * "Used for" display groups, in the order the Providers & Models model modal
 * shows them (Text / Image / Video · Voice / Data). Only the CURRENT vocabulary —
 * the ambiguous legacy `image` / `image_edit` strings are deliberately
 * excluded here so neither is ever offered as a pressable chip again.
 */
export const USED_FOR_DISPLAY_GROUPS: Array<{ heading: string; caps: string[] }> = [
  { heading: 'Text', caps: ['text', 'web_search', 'long_form'] },
  { heading: 'Image', caps: ['draws_images', 'edits_images', 'finds_images', 'reads_images'] },
  { heading: 'Video · Voice', caps: ['video', 'voice'] },
  { heading: 'Data', caps: ['property_data', 'area_data', 'maps'] },
];

export const SELECTABLE_USED_FOR: string[] = USED_FOR_DISPLAY_GROUPS.flatMap((g) => g.caps);

/**
 * Normalised used-for list (known vocabulary, deduped, stable order),
 * leftover unrecognised strings kept as-is, and ambiguous legacy strings
 * (plain `image`) separated into `needsReview` — they never count toward a
 * capability, but the caller decides whether to keep or drop them on save
 * (see NEEDS_REVIEW_CAPABILITIES).
 */
export function normalizeUsedFor(capabilities: string[] | undefined, vocabulary: readonly string[]): {
  usedFor: string[]; other: string[]; needsReview: string[];
} {
  const used = new Set<string>();
  const other: string[] = [];
  const needsReview: string[] = [];
  for (const raw of capabilities ?? []) {
    const c = String(raw).trim();
    if (!c) continue;
    const alias = LEGACY_CAPABILITY_ALIASES[c];
    if (alias) { used.add(alias); continue; }
    if (NEEDS_REVIEW_CAPABILITIES.includes(c)) { needsReview.push(c); continue; }
    if (vocabulary.includes(c)) { used.add(c); continue; }
    if (!other.includes(c)) other.push(c);
  }
  return { usedFor: vocabulary.filter((v) => used.has(v)), other, needsReview };
}

/** Which "used for" values satisfy each action group. Plain `image` never matches — it is needs-review, not a capability. */
export const GROUP_USED_FOR: Record<AiActionGroupId, string[]> = {
  text: ['text', 'web_search', 'long_form'],
  image: ['draws_images', 'edits_images'],
  video: ['video'],
  voice: ['voice'],
};

export const GROUP_LABELS: Record<AiActionGroupId, string> = {
  text: 'Text', image: 'Image', video: 'Video', voice: 'Voice',
};

export const ACTION_GROUP_ORDER: AiActionGroupId[] = ['text', 'image', 'video', 'voice'];

/**
 * A model matches a group only via a resolved used-for value — `needsReview`
 * values (plain `image`) are excluded from `usedFor`, so an ambiguous row
 * never silently satisfies the image group until an admin retags it.
 */
export function modelMatchesGroup(model: AiModelDto, group: AiActionGroupId, vocabulary: readonly string[]): boolean {
  const { usedFor } = normalizeUsedFor(model.capabilities, vocabulary);
  return usedFor.some((u) => GROUP_USED_FOR[group].includes(u));
}

export function modelsForGroup(models: AiModelDto[], group: AiActionGroupId, vocabulary: readonly string[]): AiModelDto[] {
  return models.filter((m) => modelMatchesGroup(m, group, vocabulary));
}

/**
 * Step-level capability match (2026-10-04, Ruby Generation Engine): a step
 * names ONE "used for" capability directly (e.g. `draws_images`,
 * `edits_images`) rather than a whole action group, so a "Draws with" picker
 * never offers a background-removal-only model and vice versa.
 */
export function modelHasCapability(model: AiModelDto, capability: string, vocabulary: readonly string[]): boolean {
  return normalizeUsedFor(model.capabilities, vocabulary).usedFor.includes(capability);
}

/** Models with a given step capability — includes disabled models (callers grey them, never hide them). */
export function modelsWithCapability(models: AiModelDto[], capability: string, vocabulary: readonly string[]): AiModelDto[] {
  return models.filter((m) => modelHasCapability(m, capability, vocabulary));
}

// ---------- bill unit & vendor price ----------

const has = (p: AiUnitPricesDto, k: string) => typeof p[k] === 'number' && (p[k] as number) > 0;

/** "Bills by" — derived from which unitPrices keys are set (spec §1). */
export function billsBy(unitPrices: AiUnitPricesDto | undefined): string {
  const p = unitPrices ?? {};
  const units: string[] = [];
  if (has(p, 'input_per_million_usd') || has(p, 'output_per_million_usd')) units.push('Tokens');
  if (has(p, 'per_image_usd')) units.push('Per image');
  if (has(p, 'per_second_usd')) units.push('Per second');
  if (has(p, 'per_call_usd')) units.push('Per call');
  return units.length ? units.join(' + ') : '—';
}

const money = (n: number) => `$${Number(n.toPrecision(4))}`;

export function vendorPriceLabel(unitPrices: AiUnitPricesDto | undefined): string {
  const p = unitPrices ?? {};
  const parts: string[] = [];
  if (has(p, 'input_per_million_usd') || has(p, 'output_per_million_usd')) {
    parts.push(`${money(p.input_per_million_usd ?? 0)} in / ${money(p.output_per_million_usd ?? 0)} out per 1M`);
  }
  if (has(p, 'per_image_usd')) parts.push(`${money(p.per_image_usd!)} / image`);
  if (has(p, 'per_second_usd')) parts.push(`${money(p.per_second_usd!)} / second`);
  if (has(p, 'per_call_usd')) parts.push(`${money(p.per_call_usd!)} / call`);
  return parts.length ? parts.join(' · ') : 'No price set';
}

export function isPriced(m: Pick<AiModelDto, 'unitPrices'>): boolean {
  return Object.values(m.unitPrices ?? {}).some((v) => typeof v === 'number' && v > 0);
}

// ---------- model refs ----------

export const modelRef = (providerId?: string | null, modelId?: string | null): string =>
  providerId && modelId ? `${providerId}#${modelId}` : '';

export function parseModelRef(ref: string | null | undefined): { providerId: string; modelId: string } | null {
  if (!ref) return null;
  const i = ref.indexOf('#');
  if (i <= 0 || i === ref.length - 1) return null;
  return { providerId: ref.slice(0, i), modelId: ref.slice(i + 1) };
}

export function findModel(models: AiModelDto[], providerId?: string, modelId?: string): AiModelDto | undefined {
  return models.find((m) => m.providerId === providerId && m.modelId === modelId);
}

export function modelDisplayName(models: AiModelDto[], providerId?: string, modelId?: string): string {
  if (!providerId || !modelId) return '—';
  return findModel(models, providerId, modelId)?.label ?? `${providerId}/${modelId}`;
}

// ---------- action status ----------

/**
 * Actions with a generator in code (pos-agents WIRED_ACTIONS).
 * `image.single_object` joined 2026-10-04 (Ruby Generation Engine Task 16):
 * draw → background-removal pipeline, media-service.
 */
export const WIRED_ACTION_IDS = new Set([
  'text.product_description', 'image.single_object',
  // Careers (2026-10-08): job post from a note (pos-agents) + advert photo (media-service engine).
  'text.job_description', 'image.job_ad',
  // Ruby Estate property hub (2026-10-11): RentCast data actions + Ruby's take (pos-agents-ext).
  'data.property_lookup', 'data.property_valuation', 'data.rent_estimate', 'text.property_analysis',
  // Product Intelligence (2026-10-11): restock forecast on the daily scan + Rescan (pos-agents).
  'text.product_intelligence',
  // RubyTill identity verification (2026-10-15, Tasks 7-13): identity-service
  // — create/reveal/export/import checks + the daily storage-meter job.
  // identity.age_check stays OUT: feature is seeded `coming_soon` and no
  // storefront surface calls it yet — it joins once the storefront age-check
  // flow is built.
  'identity.document_check', 'identity.ssn_check', 'identity.export', 'identity.import', 'identity.storage',
  // Document Management storage meter (2026-10-17, phase 1B): procurement-service
  // `storageMeter` monthly charge — documents / product images / requests / egress.
  'storage.documents', 'storage.product_images', 'storage.requests', 'storage.egress',
  // Document Management — Ruby reads the invoice (2026-10-17): procurement-service
  // POST /documents/{id}/ruby-read (gemini vision via ai-router).
  'documents.read_invoice',
  // Ruby Generation Engine steps (media-service src/engine) — their registry rows carry no
  // `runs`, so they stay listed here: storefront/creator compositions, video and voice clips.
  'image.multiple_objects', 'video.clip', 'voice.clip',
]);

/**
 * AI calls found in code that are not an action yet (mockup board 2). The
 * `candidateFor` ones are the reason an action shows TO MAP: the code exists,
 * it has not been routed through the action.
 */
export const UNMAPPED_AI_CALLS: Array<{ service: string; call: string; note?: string; candidateFor?: string }> = [
  { service: 'uploads-service', call: 'imageEnhance', note: 'vision', candidateFor: 'image.single_object' },
  { service: 'media-service', call: 'processBackgroundVertex', note: 'background removal', candidateFor: 'image.single_object' },
  { service: 'creator-service', call: 'wordArt', note: 'banners', candidateFor: 'image.multiple_objects' },
  { service: 'creator-service', call: 'menuBoards', note: 'banners', candidateFor: 'image.multiple_objects' },
  { service: 'pos-agents', call: 'image-search-only' },
  { service: 'pos-agents', call: 'product-assist' },
  { service: 'pos-agents', call: 'watermark' },
  { service: 'pos-agents', call: 'onboarding-generate' },
  { service: 'pos-agents', call: 'analytics query' },
  { service: 'uploads-service', call: 'routineGenerate' },
  { service: 'support-service', call: 'issue-fix' },
];

export type ActionStatus = 'live' | 'off' | 'not_wired' | 'to_map';

export const ACTION_STATUS_LABEL: Record<ActionStatus, string> = {
  live: 'LIVE', off: 'OFF', not_wired: 'NOT WIRED', to_map: 'TO MAP',
};

/**
 *  LIVE      — has a generator in code and is enabled.
 *  OFF       — has a generator but is switched off.
 *  TO MAP    — no usable model for its group, or AI code exists for it that
 *              has not been routed through the action yet.
 *  NOT WIRED — has a model, but no generator in code yet.
 */
export function actionStatus(action: AiActionDto, models: AiModelDto[], vocabulary: readonly string[]): ActionStatus {
  // Wired = the code calls it. The registry row names its caller (`runs.service`, written
  // when the action is wired) — read that, so a new action never shows "not wired" just
  // because this hand list was not updated (10-18: 19 working actions showed as unwired).
  if (WIRED_ACTION_IDS.has(action.actionId) || action.runs?.service) return action.enabled ? 'live' : 'off';
  const m = findModel(models, action.providerId, action.modelId);
  if (!m || !modelMatchesGroup(m, action.group, vocabulary)) return 'to_map';
  if (UNMAPPED_AI_CALLS.some((c) => c.candidateFor === action.actionId)) return 'to_map';
  return 'not_wired';
}

/** Billing unit for an action row subtitle ("per token", "per image"). */
export function actionUnit(action: AiActionDto, models: AiModelDto[]): string {
  const m = findModel(models, action.providerId, action.modelId);
  const b = billsBy(m?.unitPrices);
  if (b !== '—') return `per ${b.toLowerCase().replace(/^per /, '').replace('tokens', 'token')}`;
  return { text: 'per token', image: 'per image', video: 'per second', voice: 'per second' }[action.group];
}

// ---------- pricing (spec §3, the one formula) ----------

/** margin = 1 − 1/multiplier (share of revenue that is not vendor cost). */
export function marginFromMultiplier(multiplier: number | undefined | null): number | null {
  if (!multiplier || !Number.isFinite(multiplier) || multiplier <= 0) return null;
  return 1 - 1 / multiplier;
}

export const pct = (x: number | null | undefined, digits = 0): string =>
  x == null || !Number.isFinite(x) ? '—' : `${(x * 100).toFixed(digits)}%`;

/**
 * charge_credits = clamp(ceil(vendorCost × multiplier / creditUnitPrice),
 *                        minTokens/tokensPerCredit, maxTokens/tokensPerCredit)
 * tokensPerCredit defaults to the LIVE rate (AI → Settings), 1,000 by default.
 * Returns STORED credits and the Ruby Tokens a tenant would see.
 */
export function estimateCharge(args: {
  vendorCostUsd: number; multiplier: number; minTokens: number; maxTokens: number;
  creditUnitPriceUsd?: number; tokensPerCredit?: number;
}): { credits: number; rubyTokens: number; clamped: 'min' | 'max' | null } {
  const unit = args.creditUnitPriceUsd && args.creditUnitPriceUsd > 0 ? args.creditUnitPriceUsd : 0.01;
  const per = args.tokensPerCredit ?? liveTokensPerCredit();
  // Same rounding as pos-agents computeApiCredits: strip IEEE-754 noise at
  // 1e-6 before the ceiling (0.035 × 2 × 100 must charge 7, not 8).
  const raw = Math.ceil(Math.round(((args.vendorCostUsd * args.multiplier) / unit) * 1e6) / 1e6);
  const min = Math.ceil(args.minTokens / per);
  const max = Math.max(Math.ceil(args.maxTokens / per), min);
  let credits = raw;
  let clamped: 'min' | 'max' | null = null;
  if (credits < min) { credits = min; clamped = 'min'; }
  if (credits > max) { credits = max; clamped = 'max'; }
  return { credits, rubyTokens: credits * per, clamped };
}

export function effectiveMultiplier(vibe: Pick<AiVibeDto, 'multiplier'> | null | undefined, action: Pick<AiActionDto, 'multiplier'>): number {
  return typeof vibe?.multiplier === 'number' ? vibe.multiplier : action.multiplier;
}

/**
 * One model's typical vendor cost from its stored `typicalUsage` × its
 * `unitPrices` — the same per-unit formula as `vendorPriceLabel`, summed
 * across every priced unit the model actually uses. `null` when there is no
 * typical-usage data to price from (the caller skips it, never treats it as
 * free).
 */
export function modelTypicalCostUsd(m: Pick<AiModelDto, 'unitPrices'> & { typicalUsage?: AiUsageCountsDto }): number | null {
  const u = m.typicalUsage;
  if (!u) return null;
  const p = m.unitPrices ?? {};
  let cost = 0;
  let any = false;
  const add = (qty: number | undefined, priceKey: keyof AiUnitPricesDto, perMillion = false) => {
    const price = p[priceKey];
    if (typeof price !== 'number' || !qty) return;
    cost += perMillion ? (qty / 1_000_000) * price : qty * price;
    any = true;
  };
  add(u.inputTokens, 'input_per_million_usd', true);
  add(u.outputTokens, 'output_per_million_usd', true);
  add(u.cacheWriteTokens, 'cache_write_per_million_usd', true);
  add(u.cacheReadTokens, 'cache_read_per_million_usd', true);
  add(u.images, 'per_image_usd');
  add(u.calls, 'per_call_usd');
  add(u.seconds, 'per_second_usd');
  return any ? cost : null;
}

/**
 * Vendor cost of a multi-step pipeline (spec: mirrors pos-agents-service's
 * server-side `quoteRun`): for each step, the MOST EXPENSIVE enabled + priced
 * candidate (primary or any fallback) sets that step's cost — the step
 * degrades to a cheaper fallback only when the primary actually fails, so the
 * estimate must not assume the fallback runs. Steps with no priced candidate
 * contribute nothing (never treated as free — `estimateCharge` downstream
 * still floors on the action's minimum).
 */
export function estimateStepsChargeUsd(steps: AiActionStepDto[], models: AiModelDto[]): number {
  let total = 0;
  for (const s of steps) {
    const refs: ModelRefDto[] = [{ providerId: s.providerId, modelId: s.modelId }, ...(s.fallbacks ?? [])];
    const costs = refs
      .map((r) => findModel(models, r.providerId, r.modelId))
      .filter((m): m is AiModelDto => !!m && m.enabled && isPriced(m))
      .map((m) => modelTypicalCostUsd(m))
      .filter((c): c is number => c != null);
    if (costs.length) total += Math.max(...costs);
  }
  return total;
}

/**
 * Typical vendor cost of one run, from real spend: the vibe's own average
 * (tenant + admin test runs) when it has any, else the action's, else null.
 */
export function typicalRunCost(
  usage: {
    byAction: Array<{ actionId: string; runs: number; vendorCostUsd: number }>;
    byVibe: Array<{ actionId: string; vibeId: string; runs: number; vendorCostUsd: number }>;
    adminTests: Array<{ actionId: string; vibeId: string; runs: number; vendorCostUsd: number }>;
  } | null,
  actionId: string,
  vibeId?: string | null,
): { costUsd: number; runs: number; basis: 'vibe' | 'action' } | null {
  if (!usage) return null;
  if (vibeId) {
    const rows = [...usage.byVibe, ...usage.adminTests].filter((r) => r.actionId === actionId && r.vibeId === vibeId);
    const runs = rows.reduce((a, r) => a + (r.runs || 0), 0);
    if (runs > 0) return { costUsd: rows.reduce((a, r) => a + (r.vendorCostUsd || 0), 0) / runs, runs, basis: 'vibe' };
  }
  const a = usage.byAction.find((r) => r.actionId === actionId);
  if (a && a.runs > 0) return { costUsd: a.vendorCostUsd / a.runs, runs: a.runs, basis: 'action' };
  return null;
}

// ---------- tiers (read from the AI family, never constants) ----------

export interface FamilyTier {
  id: string;
  name: string;
  status?: string;
  basePrice?: number;
  features?: string[];
  tierRank?: number;
  entitlements?: Array<Record<string, any>>;
  /** Set on `/packages` (Package Tiers, 2026-10-11) — preferred over the "Name — Suffix" split below. */
  tierLabel?: string;
}

/**
 * "Ruby AI — Pro" → "Pro". A `tierLabel` set on `/packages` (Plan B Task B6)
 * always wins — it is the admin's own name for the tier, and the only one a
 * tier created there carries. The top tier without a suffix and without a
 * label is Enterprise (D13).
 */
export function shortTierName(tier: FamilyTier, isTop: boolean): string {
  if (tier.tierLabel && tier.tierLabel.trim()) return tier.tierLabel.trim();
  const m = tier.name?.split(/\s[—–-]\s/);
  if (m && m.length > 1 && m[m.length - 1].trim()) return m[m.length - 1].trim();
  return isTop ? 'Enterprise' : tier.name || tier.id;
}

export function sortTiers(tiers: FamilyTier[]): FamilyTier[] {
  return [...tiers].sort((a, b) => (a.tierRank ?? 0) - (b.tierRank ?? 0));
}

/** Tier names that unlock `featureId`, lowest first. No feature → every tier. */
export function tiersUnlocking(tiers: FamilyTier[], featureId: string | undefined | null): string[] {
  const sorted = sortTiers(tiers);
  const topId = sorted[sorted.length - 1]?.id;
  return sorted
    .filter((t) => !featureId || (t.features ?? []).includes(featureId))
    .map((t) => shortTierName(t, t.id === topId));
}

/**
 * "Unlocked on" for a vibe on an action: the vibe's own feature gate if it
 * has one, AND the action's gate (a vibe can never be used without its action).
 */
export function vibeUnlockedOn(tiers: FamilyTier[], action: Pick<AiActionDto, 'featureId'>, vibe: Pick<AiVibeDto, 'featureId'>): string {
  if (!tiers.length) return '—';
  const sorted = sortTiers(tiers);
  const topId = sorted[sorted.length - 1]?.id;
  const names = sorted
    .filter((t) => (t.features ?? []).includes(action.featureId) && (!vibe.featureId || (t.features ?? []).includes(vibe.featureId)))
    .map((t) => shortTierName(t, t.id === topId));
  if (!names.length) return 'No tier';
  if (names.length === 1) return `${names[0]} only`;
  return names.join(' · ');
}

/** The `ruby_tokens` meter's included allowance (Ruby Tokens) — null = unlimited, undefined = no meter. */
export function includedRubyTokens(tier: FamilyTier): number | null | undefined {
  const rule = (tier.entitlements ?? []).find((r) => r?.kind === 'meter' && r?.resource === 'ruby_tokens');
  if (!rule) return undefined;
  return rule.included === null ? null : Number(rule.included);
}

/** Replace (or add) the ruby_tokens meter, keeping every other rule untouched. */
export function withIncludedRubyTokens(entitlements: Array<Record<string, any>> | undefined, included: number | null): Array<Record<string, any>> {
  const rules = [...(entitlements ?? [])];
  const i = rules.findIndex((r) => r?.kind === 'meter' && r?.resource === 'ruby_tokens');
  const next = { overage: { action: 'wallet' }, ...(i >= 0 ? rules[i] : {}), kind: 'meter', resource: 'ruby_tokens', included };
  if (i >= 0) rules[i] = next; else rules.push(next);
  return rules;
}

/** One pending write: replace a tier package's `ruby_tokens` meter. */
export interface TokenDraftOp { tierId: string; entitlements: Array<Record<string, any>> }

/**
 * Super Admin /packages (Plan B Task B6) edits Monthly Ruby Tokens per tier as
 * a `Record<rank, string>` draft (rank = the tier's 1|2|3 position, same as
 * `TierDraft.rank`; empty string = unlimited). PURE — no I/O: the caller
 * fetches the family's CURRENT tiers (after `runTierSave`, so real ids exist
 * for any tier just turned on) and this only decides which of them actually
 * changed and what their new `entitlements` array must be, so a save never
 * re-writes an untouched tier.
 *
 * Each draft is resolved to its tier through its OWN `TierDraft.id` — the id
 * the editor already holds from `tiersForPackage` — never through position
 * in `family.tierIds` and never through a tier's own `tierRank` field (final
 * review I7: dev data has two tiers in one family sharing `tierRank: 3`, and
 * a corrupted/out-of-order `family.tierIds` can point position `rank-1` at
 * the wrong package entirely — either would write the same entitlements to
 * the wrong tier, or overwrite one whose draft never changed). A draft
 * created in THIS save has no `id` yet; `createdIds[rank]` — the id
 * `runTierSave` just created for that rank — is used instead.
 */
export function planTokenSaveOps(
  tiers: FamilyTier[],
  drafts: ReadonlyArray<{ rank: 1 | 2 | 3; id?: string }>,
  tokenDrafts: Record<number, string>,
  createdIds: Record<number, string> = {},
): TokenDraftOp[] {
  const byId = new Map(tiers.map((t) => [t.id, t] as const));
  const ops: TokenDraftOp[] = [];
  for (const d of drafts) {
    const rank = d.rank;
    if (!(rank in tokenDrafts)) continue;
    const tierId = d.id ?? createdIds[rank];
    const t = tierId ? byId.get(tierId) : undefined;
    if (!t) continue; // that draft's tier doesn't exist yet — nothing to write
    const raw = (tokenDrafts[rank] ?? '').trim();
    const current = includedRubyTokens(t);
    const currentStr = current == null ? '' : String(current);
    if (raw === currentStr) continue; // unchanged — never re-written
    if (raw !== '' && (!/^\d+$/.test(raw) || !Number.isSafeInteger(Number(raw)))) continue; // invalid — never sent
    const value = raw === '' ? null : Number(raw);
    ops.push({ tierId: t.id, entitlements: withIncludedRubyTokens(t.entitlements, value) });
  }
  return ops;
}

// ---------- seasons ----------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTH_NAMES = MONTHS;

export function monthDayLabel(md: string): string {
  const [m, d] = md.split('-').map(Number);
  if (!m || !d || m < 1 || m > 12) return md;
  return `${MONTHS[m - 1]} ${d}`;
}

export function seasonLabel(season: AiVibeDto['season']): string {
  if (!season) return 'Always';
  return `${monthDayLabel(season.fromMonthDay)} – ${monthDayLabel(season.toMonthDay)}`;
}

export const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const monthDay = (month: number, day: number): string =>
  `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// ---------- features ----------

export const AI_FEATURE_LABELS: Record<string, string> = {
  'ai-description': 'Product description',
  'ai-careers-actions': 'Careers (job posts & adverts)',
  'ai-vibes-seasonal': 'Seasonal vibes',
  'ai-vibes-premium': 'Premium vibes',
  'ai-image-actions': 'Image actions',
  'ai-legal-actions': 'Legal actions',
  'ai-video-actions': 'Video actions',
  'ai-voice-actions': 'Voice actions',
  'pos.agents.forecasting': 'Product Intelligence (Product Alerts)',
  'documents.invoices': 'Document Management',
  'ai-documents-actions': 'Documents (Ruby reads invoices)',
  'storage.product_images': 'Product images (every tenant, 1 GB free)',
};
